import { useTheme } from '@material-ui/core/styles'
import Add from '@material-ui/icons/Add'
import DeleteOutlined from '@material-ui/icons/DeleteOutlined'
import { CurrencySymbolContext } from 'contexts/CurrencySymbolContext'
import { IconButton } from 'opensolar-ui'
import { Button } from 'ra-ui-materialui'
import { FC, useContext } from 'react'
import { NumberInput, SelectInput, useTranslate } from 'react-admin'
import { useForm } from 'react-final-form'
import { ADJUSTMENT_TO_OPTIONS, SINGLE_TIER_OPTIONS, UNIT_OPTIONS } from 'resources/utilityTariffs/common'
import { tableStyles } from 'resources/utilityTariffs/styles'
import { getTariffInputOptions } from './inputOptions'
import { PeriodTier } from './types'

type PropsType = {
  source: string
  isLast: boolean
  tierSource: string
  tierIndex: number
  disabled?: boolean
}

const TierQuantityField: FC<PropsType> = ({ source, isLast, tierSource, tierIndex, disabled }) => {
  const classes = tableStyles()
  const theme = useTheme()
  const translate = useTranslate()
  if (SINGLE_TIER_OPTIONS.includes(source) || isLast) {
    return <div className={`${classes.tierFieldRow} ${classes.tierFieldRowMid}`} />
  }
  return (
    <div className={`${classes.tierFieldRow} ${classes.tierFieldRowMid}`}>
      <span className={classes.tierFieldLabel}>{translate('Tier Quantity')}</span>
      <div className={classes.tierFieldInput}>
        <NumberInput
          label={false}
          source={`${tierSource}.${tierIndex}.limit`}
          variant="outlined"
          options={getTariffInputOptions(theme)}
          disabled={disabled}
          className={classes.rateInput}
        />
      </div>
    </div>
  )
}

type TiersProps = {
  tiers: PeriodTier[]
  source: string
  tierSource: string
  adjustment: boolean
  disabled?: boolean
  isSingleTier?: boolean
}

const RatePeriodTiers: FC<TiersProps> = ({ tiers, source, tierSource, adjustment, disabled, isSingleTier }) => {
  const form = useForm()
  const classes = tableStyles()
  const theme = useTheme()
  const translate = useTranslate()
  const sourceName = source.replace('tariffData.', '')
  const currencySymbol = useContext(CurrencySymbolContext)
  const tiered = tierSource.includes('blocks')
  const handleAddTier = () => {
    const defaultUnits = !!tiers.length ? tiers[tiers.length - 1].units : null
    form.mutators.updateField(tierSource, [...tiers, { value: 0, units: defaultUnits, limit: 0 }])
  }
  const handleDeleteTier = (index) => {
    let currList = [...tiers]
    currList.splice(index, 1)
    form.mutators.updateField(tierSource, currList)
  }
  return (
    <>
      {tiers?.map((rateTier, i) => {
        const updateSource = tiered ? `${tierSource}.${i}` : tierSource
        return (
          <div
            className={isSingleTier ? `${classes.periodTier} ${classes.periodTierSingleTier}` : classes.periodTier}
            key={i}
          >
            <div className={classes.tierLabelColumn}>
              {tiers?.length > 1 ? <span className={classes.tierLabel}>Tier {i + 1}</span> : null}
            </div>
            <div className={`${classes.tierFieldRow} ${classes.tierFieldRowPrice}`}>
              <span className={classes.tierFieldLabel}>{translate(adjustment ? 'Amount %' : 'Price')}</span>
              <div className={classes.tierFieldInput}>
                <NumberInput
                  label={false}
                  startAdornment={!adjustment ? currencySymbol : undefined}
                  endAdornment={adjustment ? '%' : undefined}
                  source={`${updateSource}.value`}
                  className={classes.rateInput}
                  variant="outlined"
                  options={getTariffInputOptions(theme)}
                  disabled={disabled}
                />
              </div>
            </div>
            {adjustment ? (
              <div className={`${classes.tierFieldRow} ${classes.adjustmentContainer}`}>
                <span className={classes.tierFieldLabel}>{translate('Adjustment To')}</span>
                <div className={classes.tierFieldInput}>
                  <SelectInput
                    label={false}
                    disabled={disabled}
                    source={`${updateSource}.adjustment_to`}
                    className={classes.rateInput}
                    variant="outlined"
                    options={getTariffInputOptions(theme)}
                    optionText={(record: any) => {
                      return `${currencySymbol}/${record?.name}`
                    }}
                    fullWidth
                    choices={ADJUSTMENT_TO_OPTIONS}
                  />
                </div>
              </div>
            ) : (
              <>
                {!isSingleTier && (
                  <TierQuantityField
                    source={sourceName}
                    isLast={i === tiers?.length - 1}
                    tierIndex={i}
                    tierSource={tierSource}
                    disabled={disabled}
                  />
                )}
                <div className={`${classes.tierFieldRow} ${classes.tierFieldRowUnits}`}>
                  <span className={classes.tierFieldLabel}>{translate('Units')}</span>
                  <div className={classes.tierFieldInput}>
                    <SelectInput
                      label={false}
                      source={`${updateSource}.units`}
                      className={`${classes.rateInput} ${classes.tierInput}`}
                      variant="outlined"
                      options={getTariffInputOptions(theme)}
                      optionText={(record: any) => {
                        return `${currencySymbol}/${record?.name}`
                      }}
                      disabled={disabled}
                      fullWidth
                      choices={UNIT_OPTIONS[sourceName]}
                    />
                  </div>
                </div>
              </>
            )}
            <div className={classes.deleteContainer}>
              {tiers?.length > 1 && (
                <>
                  <span className={classes.deleteTierIconOnly}>
                    <IconButton disabled={disabled} onClick={() => handleDeleteTier(i)} className={classes.deleteTier}>
                      <DeleteOutlined />
                    </IconButton>
                  </span>
                  <span className={classes.deleteTierFullWidthBtn}>
                    <Button
                      onClick={() => handleDeleteTier(i)}
                      startIcon={<DeleteOutlined />}
                      label={translate('Remove tier')}
                      hideLabelAt={null}
                      variant="outlined"
                      color="default"
                      disabled={disabled}
                      className={classes.deleteTierFullWidth}
                    />
                  </span>
                </>
              )}
            </div>
          </div>
        )
      })}
      {tiered && !adjustment && !SINGLE_TIER_OPTIONS.includes(sourceName) && (
        <div className={classes.periodTier}>
          <div className={classes.tierLabelColumn} />
          <div className={classes.tierInputWithLabel} />
          <div className={classes.midInputContainer} />
          <div className={`${classes.tierInput} ${classes.addTierRow}`}>
            <Button
              onClick={handleAddTier}
              startIcon={<Add />}
              label={'Add Tier'}
              hideLabelAt={null}
              variant="contained"
              color="default"
              disabled={disabled}
              className={classes.addTierBtn}
            />
          </div>
          <div className={classes.deleteContainer} />
        </div>
      )}
    </>
  )
}
export default RatePeriodTiers
